import { Check, Copy } from "lucide-react";
import React, { useState } from "react";
import { cn } from "src/common/utils";
import { Tooltip } from "src/components/ui/tooltip";

import { processMarkdownForClipboard } from "./processMarkdownForClipboard";

interface ChatMessageClipboardProps {
  messageContent: string;
  className?: string;
}

export const ChatMessageClipboard: React.FC<ChatMessageClipboardProps> = ({
  messageContent,
  className
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleCopyToClipboard = async () => {
    setIsAnimating(true);

    try {
      // Process the markdown content before copying
      const processedContent = await processMarkdownForClipboard(messageContent);

      await navigator.clipboard.writeText(processedContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
      // Fallback: copy unprocessed content if processing fails
      try {
        await navigator.clipboard.writeText(messageContent);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      } catch (fallbackErr) {
        console.error("Failed to copy fallback text: ", fallbackErr);
      }
    } finally {
      setTimeout(() => setIsAnimating(false), 20);
    }
  };

  const buttonBaseClasses = "p-1.5 rounded-md transition-colors duration-150 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed";
  const iconSize = "h-4 w-4";

  return (
    <div className={cn("flex items-center", className)}>
      <Tooltip content={isCopied ? "Copied!" : "Copy to clipboard"} position="top">
        <button
          onClick={handleCopyToClipboard}
          className={cn(
            buttonBaseClasses,
            isAnimating && "bg-green-600 scale-90"
          )}
          aria-label="Copy message content"
        >
          {isCopied ? (
            <Check className={cn(iconSize, "text-green-500")} />
          ) : (
            <Copy className={cn(iconSize, "text-current")} />
          )}
        </button>
      </Tooltip>
    </div>
  );
};